import React, { useEffect, useMemo, useState } from "react";
import { api, apiErrorMessage, downloadFile } from "@/lib/api";
import { PageHeader, Section, EmptyState } from "@/components/Shell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { DAYS, getMondayISO, shiftWeek, formatWeekRange, formatKg, formatInt, PROTEIN_CATEGORIES } from "@/lib/menu-utils";
import { ChevronLeft, ChevronRight, FileSpreadsheet, FileText, Layers } from "lucide-react";

export default function Purchases() {
  const [companies, setCompanies] = useState([]);
  const [companyId, setCompanyId] = useState("");
  const [weekStart, setWeekStart] = useState(getMondayISO());
  const [tab, setTab] = useState("company");
  const [companyData, setCompanyData] = useState(null);
  const [consData, setConsData] = useState(null);
  const [loading, setLoading] = useState(false);

  const loadCompanies = async () => {
    try {
      const { data } = await api.get("/companies");
      setCompanies(data);
      if (!companyId && data.length) setCompanyId(data[0].id);
    } catch (e) { toast.error(apiErrorMessage(e)); }
  };

  const loadCompanyPurchase = async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/purchase-list/company/${companyId}`, { params: { week_start: weekStart } });
      setCompanyData(data);
    } catch (e) { toast.error(apiErrorMessage(e)); } finally { setLoading(false); }
  };

  const loadConsolidated = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/purchase-list/consolidated", { params: { week_start: weekStart } });
      setConsData(data);
    } catch (e) { toast.error(apiErrorMessage(e)); } finally { setLoading(false); }
  };

  useEffect(() => { loadCompanies(); /* eslint-disable-next-line */ }, []);
  useEffect(() => { if (tab === "company") loadCompanyPurchase(); /* eslint-disable-next-line */ }, [tab, companyId, weekStart]);
  useEffect(() => { if (tab === "consolidated") loadConsolidated(); /* eslint-disable-next-line */ }, [tab, weekStart]);

  const catLabel = (v) => PROTEIN_CATEGORIES.find(c => c.value === v)?.label || v;

  const exportCompany = (fmt) => {
    if (!companyId) return;
    const filename = `compras_${companyData?.company?.name || "empresa"}_${weekStart}.${fmt === "xlsx" ? "xlsx" : "pdf"}`;
    downloadFile(`/export/company/${companyId}/${fmt}?week_start=${weekStart}`, filename)
      .then(() => toast.success("Exportação concluída"))
      .catch(e => toast.error(apiErrorMessage(e)));
  };
  const exportConsolidated = (fmt) => {
    const filename = `compras_consolidado_${weekStart}.${fmt === "xlsx" ? "xlsx" : "pdf"}`;
    downloadFile(`/export/consolidated/${fmt}?week_start=${weekStart}`, filename)
      .then(() => toast.success("Exportação concluída"))
      .catch(e => toast.error(apiErrorMessage(e)));
  };

  if (companies.length === 0) {
    return (
      <div data-testid="purchases-page">
        <PageHeader title="Lista de Compras" />
        <Section><EmptyState title="Cadastre uma empresa primeiro" description="É necessário ter empresas cadastradas para gerar listas de compras." /></Section>
      </div>
    );
  }

  return (
    <div data-testid="purchases-page">
      <PageHeader
        title="Lista de Compras"
        subtitle="Kg necessários calculados a partir do cardápio da semana."
        actions={
          <div className="flex items-center gap-1 bg-white border border-slate-200 rounded-md p-1">
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setWeekStart(shiftWeek(weekStart, -1))} data-testid="purch-week-prev"><ChevronLeft size={16} /></Button>
            <Input type="date" value={weekStart} onChange={(e) => setWeekStart(e.target.value)} className="h-8 w-40 border-0 focus-visible:ring-0" data-testid="purch-week-input" />
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setWeekStart(shiftWeek(weekStart, 1))} data-testid="purch-week-next"><ChevronRight size={16} /></Button>
          </div>
        }
      />

      <div className="text-xs uppercase tracking-wider text-slate-500 mb-4">
        Semana: <span className="text-slate-700 font-medium">{formatWeekRange(weekStart)}</span>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="mb-4">
          <TabsTrigger value="company" data-testid="tab-company">Por empresa</TabsTrigger>
          <TabsTrigger value="consolidated" data-testid="tab-consolidated"><Layers size={14} className="mr-1" /> Consolidado (todas)</TabsTrigger>
        </TabsList>

        <TabsContent value="company">
          <div className="grid md:grid-cols-2 gap-3 mb-4">
            <div className="bg-white border border-slate-200 rounded-lg p-3">
              <div className="text-xs uppercase tracking-wide text-slate-500 font-semibold mb-1">Empresa</div>
              <Select value={companyId} onValueChange={setCompanyId}>
                <SelectTrigger className="h-9" data-testid="purch-company-select"><SelectValue /></SelectTrigger>
                <SelectContent>{companies.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="flex items-end justify-end gap-2">
              <Button variant="outline" onClick={() => exportCompany("xlsx")} data-testid="export-company-xlsx"><FileSpreadsheet size={15} className="mr-1"/> Excel</Button>
              <Button className="bg-emerald-900 hover:bg-emerald-800 text-white" onClick={() => exportCompany("pdf")} data-testid="export-company-pdf"><FileText size={15} className="mr-1"/> PDF</Button>
            </div>
          </div>

          {loading ? <div className="p-6 text-slate-500 text-sm">Calculando…</div> :
            !companyData ? null : (
              <>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                  <div className="mp-kpi"><div className="mp-kpi-label">Empresa</div><div className="text-lg font-semibold mt-1">{companyData.company.name}</div></div>
                  <div className="mp-kpi"><div className="mp-kpi-label">Refeições/dia</div><div className="mp-kpi-value">{formatInt(companyData.meals_per_day)}</div></div>
                  <div className="mp-kpi"><div className="mp-kpi-label">Proteínas</div><div className="mp-kpi-value">{companyData.totals_by_protein.length}</div></div>
                  <div className="mp-kpi"><div className="mp-kpi-label">Total kg (semana)</div><div className="mp-kpi-value">{formatKg(companyData.total_kg)}</div></div>
                </div>

                <Section title="Compras por proteína">
                  {companyData.totals_by_protein.length === 0 ? (
                    <EmptyState title="Sem cardápio para esta semana" description="Monte o cardápio semanal desta empresa para calcular as compras." />
                  ) : (
                    <table className="mp-table w-full" data-testid="purchase-company-table">
                      <thead><tr><th>Proteína</th><th>Categoria</th><th className="mp-num">Kg necessário</th></tr></thead>
                      <tbody>
                        {companyData.totals_by_protein.map(r => (
                          <tr key={r.protein_id}>
                            <td className="font-medium">{r.protein_name}</td>
                            <td className="text-slate-600">{catLabel(r.category)}</td>
                            <td className="mp-num tabular-nums font-medium">{formatKg(r.kg)}</td>
                          </tr>
                        ))}
                        <tr className="bg-slate-50">
                          <td colSpan={2} className="font-semibold text-right">Total</td>
                          <td className="mp-num tabular-nums font-semibold">{formatKg(companyData.total_kg)}</td>
                        </tr>
                      </tbody>
                    </table>
                  )}
                </Section>

                {companyData.breakdown.length > 0 && (
                  <div className="mt-6">
                    <Section title="Detalhamento diário">
                      <table className="mp-table w-full">
                        <thead>
                          <tr><th>Dia</th><th>Opção</th><th>Preparação</th><th>Proteína</th><th className="mp-num">g/ref.</th><th className="mp-num">Ref.</th><th className="mp-num">Kg</th></tr>
                        </thead>
                        <tbody>
                          {companyData.breakdown.map((b) => (
                            <tr key={`${b.day}-${b.slot}`}>
                              <td>{b.day_label}</td>
                              <td>{b.slot}</td>
                              <td>{b.preparation_name}</td>
                              <td>{b.protein_name}</td>
                              <td className="mp-num tabular-nums">{b.grams_per_meal}</td>
                              <td className="mp-num tabular-nums">{formatInt(b.meals)}</td>
                              <td className="mp-num tabular-nums font-medium">{formatKg(b.kg)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </Section>
                  </div>
                )}
              </>
            )
          }
        </TabsContent>

        <TabsContent value="consolidated">
          <div className="flex justify-end gap-2 mb-4">
            <Button variant="outline" onClick={() => exportConsolidated("xlsx")} data-testid="export-cons-xlsx"><FileSpreadsheet size={15} className="mr-1"/> Excel</Button>
            <Button className="bg-emerald-900 hover:bg-emerald-800 text-white" onClick={() => exportConsolidated("pdf")} data-testid="export-cons-pdf"><FileText size={15} className="mr-1"/> PDF</Button>
          </div>

          {loading || !consData ? <div className="p-6 text-slate-500 text-sm">Calculando…</div> : (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                <div className="mp-kpi"><div className="mp-kpi-label">Empresas ativas</div><div className="mp-kpi-value">{consData.companies_count}</div></div>
                <div className="mp-kpi"><div className="mp-kpi-label">Refeições/dia (total)</div><div className="mp-kpi-value">{formatInt(consData.meals_per_day_total)}</div></div>
                <div className="mp-kpi"><div className="mp-kpi-label">Proteínas distintas</div><div className="mp-kpi-value">{consData.consolidated.length}</div></div>
                <div className="mp-kpi"><div className="mp-kpi-label">Total kg (semana)</div><div className="mp-kpi-value">{formatKg(consData.grand_total_kg)}</div></div>
              </div>

              <Section title="Compras consolidadas (para negociação com distribuidoras)">
                {consData.consolidated.length === 0 ? (
                  <EmptyState title="Sem cardápios configurados" description="Configure os cardápios semanais das empresas para gerar a lista consolidada." />
                ) : (
                  <table className="mp-table w-full" data-testid="purchase-consolidated-table">
                    <thead><tr><th>Proteína</th><th>Categoria</th><th className="mp-num">Kg total (semana)</th></tr></thead>
                    <tbody>
                      {consData.consolidated.map(r => (
                        <tr key={r.protein_id}>
                          <td className="font-medium">{r.protein_name}</td>
                          <td className="text-slate-600">{catLabel(r.category)}</td>
                          <td className="mp-num tabular-nums font-medium">{formatKg(r.kg)}</td>
                        </tr>
                      ))}
                      <tr className="bg-slate-50">
                        <td colSpan={2} className="font-semibold text-right">Total geral</td>
                        <td className="mp-num tabular-nums font-semibold">{formatKg(consData.grand_total_kg)}</td>
                      </tr>
                    </tbody>
                  </table>
                )}
              </Section>

              <div className="mt-6">
                <Section title="Detalhamento por empresa">
                  <div className="overflow-x-auto">
                    <table className="mp-table w-full" data-testid="purchase-per-company-table">
                      <thead><tr><th>Empresa</th><th className="mp-num">Ref/dia</th><th>Proteína</th><th className="mp-num">Kg</th></tr></thead>
                      <tbody>
                        {consData.per_company.map((pc) => {
                          if (pc.totals_by_protein.length === 0) {
                            return (
                              <tr key={pc.company.id}>
                                <td className="font-medium">{pc.company.name}</td>
                                <td className="mp-num tabular-nums">{formatInt(pc.meals_per_day)}</td>
                                <td className="text-slate-400 italic">— sem cardápio —</td>
                                <td className="mp-num tabular-nums">0,000</td>
                              </tr>
                            );
                          }
                          return pc.totals_by_protein.map((r, i) => (
                            <tr key={`${pc.company.id}-${r.protein_id}`}>
                              <td className="font-medium">{i === 0 ? pc.company.name : ""}</td>
                              <td className="mp-num tabular-nums">{i === 0 ? formatInt(pc.meals_per_day) : ""}</td>
                              <td>{r.protein_name}</td>
                              <td className="mp-num tabular-nums">{formatKg(r.kg)}</td>
                            </tr>
                          ));
                        })}
                      </tbody>
                    </table>
                  </div>
                </Section>
              </div>
            </>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
